import {useTranslation} from "react-i18next";
import {useEffect} from "react";
import {UserResource} from "@shared/interfaces";
import { Separator } from "@/components/ui/separator";

import {FormInput} from "@/components/FormInput";
import {useFlow} from "@/contexts/FlowContext.tsx";
import {FlowPageInterface} from "@/interfaces";
import {useExtendedUser} from "@/contexts/UserContext.tsx";
import {Form} from "@/components/ui/form.tsx";
import useFormRequest from "@/hooks/useFormRequest.ts";
import {Button} from "@/components/ui/button.tsx";
import { BadgePercent } from "lucide-react";
import {formatCurrency} from "@/utils/currency.ts";
import {localizedRebateTypeName} from "@/utils/rebate.ts";
import { useContract } from "@/hooks/useContract";
import { usePaymentPlansAvailable } from "@/hooks/payments/usePaymentPlansAvailable";
import { hasEligiblePromotions } from "@/utils/promotions.ts";
import {ContactUs} from "@/components/ContactUs.tsx";
import RefuseQuoteLink from "@/app/quote-refusal/components/refuse-quote-link";
import { normalizePhone } from "@/utils/phone.ts";

interface UserInterface {
  data: UserResource;
}

export const FlowPageSummary = (props: FlowPageInterface) => {
  const { t, i18n } = useTranslation();
  const { user, setUser } = useExtendedUser();
  const { contractId } = useFlow();
  const { 
    data: contract,
    refetch: refetchContract,
  } = useContract(contractId);
  const { data: paymentPlans } = usePaymentPlansAvailable(contractId);

  const address = contract?.relationships?.address?.data;
  const services = contract?.relationships?.belongsToManyServices?.data;
  const discounts = contract?.relationships?.rebates?.data;
  const company = contract?.relationships?.company?.data;
  // What the customer would owe, taxes included, if none of the rebates applied.
  const priceBeforeDiscounts = contract?.meta?.prices?.priceBeforeDiscounts;
  const initialTotal =
    priceBeforeDiscounts != null
      ? priceBeforeDiscounts + (contract?.meta?.prices?.taxes?.total ?? 0)
      : null;
  // On-demand-only contracts capture a card instead of agreeing to a plan, so
  // the Payment Agreement step this banner points at is never shown to them.
  const isCardCaptureOnly = Boolean(contract?.meta?.card_capture_only);
  // Only promise discounts the customer can actually collect further down the
  // flow. Announced unconditionally, the banner had MRA customers expecting a
  // rebate that no plan offered them (FD-7410).
  const hasDiscountsAtPaymentStep =
    !isCardCaptureOnly && hasEligiblePromotions(paymentPlans);


  const serviceBillingAddress = [
    address?.attributes.address_no,
    address?.attributes.address_street,
    address?.attributes.address_apt,
    address?.attributes.address_city,
    address?.attributes.address_province,
    address?.attributes.address_postalcode,
  ]
    .filter(Boolean)
    .join(", ");

  const {setValue, loading, send, response, form} = useFormRequest<
    UserInterface,
    UserInterface
  >(
    "/folloportal/me",
    "POST",
    {
      data: {
        attributes: {
          client_contact: user?.attributes.client_contact ?? "",
          client_firstname: user?.attributes.client_firstname ?? "",
          client_lastname: user?.attributes.client_lastname ?? "",
          client_phone1: normalizePhone(user?.attributes.client_phone1),
          client_mobile: normalizePhone(user?.attributes.client_mobile),
          client_email: user?.attributes.client_email ?? "",
        },
      },
    },
    () => {
      props.nextStep();
    },
    undefined,
    {
      transform: (payload) => ({
        ...payload,
        data: {
          ...payload.data,
          attributes: {
            ...payload.data.attributes,
            client_phone1: normalizePhone(payload.data.attributes.client_phone1),
            client_mobile: normalizePhone(payload.data.attributes.client_mobile),
          },
        },
      }),
    },
  );

  useEffect(() => {
    if (response) {
      setUser(response.data);
    }
  }, [response, setUser, setValue]);

  useEffect(() => {
    if (!user) {
      return;
    }
    setValue(`data.attributes.client_firstname`, user.attributes.client_firstname);
    setValue(`data.attributes.client_lastname`, user.attributes.client_lastname);
    setValue(`data.attributes.client_phone1`, normalizePhone(user.attributes.client_phone1));
    setValue(`data.attributes.client_mobile`, normalizePhone(user.attributes.client_mobile));
    setValue(`data.attributes.client_email`, user.attributes.client_email);
  }, [user, setValue]);

  useEffect(() => {
    const first = form.getValues("data.attributes.client_firstname")?.trim() ?? "";
    const last = form.getValues("data.attributes.client_lastname")?.trim() ?? "";

    const contact = `${first} ${last}`.trim().replace(/\s+/g, " ");

    setValue("data.attributes.client_contact", contact, { shouldDirty: true });
  }, [
    form.watch("data.attributes.client_firstname"),
    form.watch("data.attributes.client_lastname"),
    setValue,
    form,
  ]);


  if (!props.currentlyVisible) {
    return <></>;
  }

  return (
    <Form {...form}>
      <form onSubmit={send}>
        <div className="grid lg:grid-cols-3 grid-cols-1 gap-9">
          <div className="col-span-1 md:col-span-2">
            <div className="follo-card">
              <section className="px-8 py-7 pb-0">
                <h3 className="follo-label mb-6">{t('fe.pages.flow.steps.summary.title')}</h3>
                <div className="grid xl:grid-cols-2 gap-x-7 gap-y-3 pb-4">
                  <FormInput
                    className={"md:min-w-[360px] mb-3"}
                    form={form}
                    label={t("models.user.firstName")}
                    name="data.attributes.client_firstname"
                    placeholder={""}
                    isRequired
                  />
                  <FormInput
                    className={"md:min-w-[360px] mb-3"}
                    form={form}
                    label={t("models.user.lastName")}
                    name="data.attributes.client_lastname"
                    placeholder={""}
                    isRequired
                  />
                </div>
                <FormInput
                  className={"md:min-w-[360px] mb-6"}
                  form={form}
                  label={t("models.user.email")}
                  name="data.attributes.client_email"
                  placeholder={""}
                  isRequired
                />
                <div className="grid xl:grid-cols-2 gap-x-7 gap-y-3 pb-4">
                  <FormInput
                    className={"md:min-w-[360px] mb-3"}
                    form={form}
                    label={t("models.user.phone")}
                    name="data.attributes.client_phone1"
                    placeholder={""}
                    phone
                    description={t("models.user.descriptionNumber")}
                  />
                  <FormInput
                    className={"md:min-w-[360px] mb-3"}
                    form={form}
                    label={t("models.user.mobile")}
                    name="data.attributes.client_mobile"
                    placeholder={""}
                    phone
                    description={t("models.user.descriptionNumber")}
                  />
                </div>
              </section>
              <hr className="border-border" />
              <div className="flex flex-wrap items-center gap-3 py-4 px-6">
                <RefuseQuoteLink contract={contract} onRefused={() => refetchContract()} />
                <Button
                  className="ml-auto cursor-pointer"
                  disabled={loading}
                  type="submit"
                >
                  {t("ui.confirm")}
                </Button>
              </div>
            </div>
            <ContactUs email={company?.attributes.email} />
          </div>
          <div className="col-span-1">
            <div className="follo-card px-8 py-7">
              <section>
                <h3 className="follo-label mb-4.5">{t('fe.pages.flow.steps.summary.title2')}</h3>
                <div className={"text-sm leading-5 font-medium text-card-foreground pb-3.5"}>
                  {t("fe.pages.flow.steps.summary.details.addressTitle")}
                </div>
                <div className={"text-sm leading-5 font-normal text-card-foreground pb-6 max-w-2/3"}>
                  {serviceBillingAddress}
                </div>
                {initialTotal != null && discounts && discounts.length > 0 && (
                  <div
                    className={
                      "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-2"
                    }
                  >
                    <div className="max-w-2/3">
                      {t("fe.pages.flow.steps.summary.details.initialTotal")}
                    </div>
                    <div>{formatCurrency(initialTotal, i18n.language)}</div>
                  </div>
                )}
                {services && (
                  <>
                    <div className={"text-sm leading-5 font-medium text-card-foreground pb-3.5"}>
                      {t("fe.pages.flow.steps.summary.details.servicesTitle")}
                    </div>
                    {services.map((service, index) => {
                      return (
                        <div
                          key={index + "_" + service.id}
                          className={
                            "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-3"
                          }
                        >
                          <div className="max-w-2/3">{service.attributes.name}</div>
                          <div>{formatCurrency(service.meta.pivot.price ?? 0, i18n.language)}</div>
                        </div>
                      );
                    })}
                  </>
                )}
                {discounts && discounts.length > 0 && (
                  <>
                    <Separator className="mt-4 mb-6"/>
                    <div className={"text-sm leading-5 font-medium text-card-foreground pb-3.5"}>
                      {t("fe.pages.flow.steps.summary.details.discounts")}
                    </div>
                    {discounts.map((discount, index) => {
                      const discountName = localizedRebateTypeName(discount, i18n.language);

                      return (
                        <div
                          key={index + "_" + discount.id}
                          className={
                            "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-3"
                          }
                        >
                          <div className="max-w-2/3">
                            <div>{discountName ?? discount.attributes.note ?? "-"}</div>
                            {discountName && discount.attributes.note && (
                              <div className="text-xs leading-4 text-muted-foreground">
                                {discount.attributes.note}
                              </div>
                            )}
                          </div>
                          <div>{formatCurrency(discount.attributes.price ?? 0, i18n.language)}</div>
                        </div>
                      );
                    })}
                  </>
                )}
                <Separator className="mt-4 mb-6"/>
                <div
                  className={
                    "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-4"
                  }
                >
                  <div>{t("fe.pages.flow.steps.summary.details.subTotal")}</div>
                  <div>{formatCurrency(contract?.meta?.prices.priceSubtotal ?? 0, i18n.language)}</div>
                </div>
                {contract?.meta?.prices.taxes.taxes.map((tax, index) => {
                  return (
                    <div
                      key={index + "_" + tax.code}
                      className={
                        "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-4"
                      }
                    >
                      <div>{tax.label}</div>
                      <div>{formatCurrency(tax.amount ?? 0, i18n.language)}</div>
                  </div>
                  )
                })}
                <div
                  className={
                    "flex justify-between text-sm leading-5 font-bold text-primary pb-4"
                  }
                >
                  <div>{t("fe.pages.flow.steps.summary.details.total")}</div>
                  <div className={"text-base leading-6 font-bold"}>
                    {formatCurrency(contract?.meta?.prices.total ?? 0, i18n.language)}
                  </div>
                </div>

                {hasDiscountsAtPaymentStep && (
                  <div className="flex text-sm text-secondary-foreground bg-background-secondary py-3 px-4 rounded-lg">
                    <BadgePercent className="h-4 w-4 mr-3 mt-0.5"/>
                    <p>{t("fe.pages.flow.steps.summary.details.discountDescription1")}
                      <br />
                      <b>{t("fe.pages.flow.steps.summary.details.discountDescription2")}</b>.
                    </p>
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};

